// @flow
import * as React from 'react';
import { Map, Set, List, OrderedMap } from 'immutable';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import { AppConfigState } from 'ducks/appConfigReducer';
import SearchForm, {
  type FieldsetConfig,
  type SearchFormValue,
} from './SearchForm';
import SearchFormHelper from './SearchForm/SearchFormHelper';
import {
  codesIncluded,
  codesExcluded,
  statusIncluded,
  statusExcluded,
  catalog as catalogFieldConfig,
} from './SearchForm/config/fields.config';
import type { ContextRouter } from 'react-router-dom';
import type { AppState } from 'reducer';
import subWeeks from 'date-fns/sub_weeks';
import { Observation } from 'models';
import { nativeSearch, ugcSearch } from 'api/content/search';
import SearchCollection from './SearchCollection';
import ClientModerationCard from 'containers/ClientCard';
import {
  type ClientProps,
  ClientContext,
} from 'containers/client/ClientProvider';
import saga from './searchSaga';
import reducer, { type SearchState, isSearching } from './searchReducer';
import { submitSearch } from './searchActions';
import { injectSaga, injectReducer } from 'store';
import Analytics from 'lib/analytics';

injectReducer(reducer);
injectSaga(saga);

type SelectOption = {
  label: Id,
  value: Id,
};
type SelectOptions = Array<SelectOption>;

type OwnProps = {
  label: string,
  primaryFieldsets: Array<FieldsetConfig>,
  extraFieldsets?: Array<FieldsetConfig>,
  onSubmit?: SearchFormValue => SearchFormValue,
  method?: Function,
  CardComponent: ?React.Element<any>,
  copy?: ?React.Node,
};

type StateProps = {
  clientId: Id,
  catalogs: $PropertyType<ClientProps, 'catalogs'>,
  observationByCode: $PropertyType<AppConfigState, 'observationsMap'>,
  query: { [string]: string },
  isSearching: boolean,
};

type DispatchProps = {
  submitSearch: submitSearch,
};
type Props = OwnProps & StateProps & DispatchProps & ContextRouter;

type State = {
  value: SearchFormValue,
  expanded: boolean,
};

const StatusObservationCodes = [
  Observation.PowerModerationCode,
  Observation.ClientModerationCode,
  Observation.PublicationOverrideCode,
  Observation.NotForPublicationOverrideCode,
];
// const StatusObservationCodesSet = Set(StatusObservationCodes);

export class SearchView extends React.PureComponent<Props, State> {
  state: State;
  codeOptions: SelectOptions;
  statusOptions: SelectOptions;
  helper: SearchFormHelper;

  constructor(props: Props) {
    super(props);

    this.helper = new SearchFormHelper(
      props.primaryFieldsets,
      props.extraFieldsets
    );

    this.state = props.location.search
      ? createStateFromSearch(this.helper, props.location.search)
      : createDefaultState(this.helper);

    if (this.props.location.search) {
      this.submit(this.state.value);
    }

    // cache code & status options
    this.codeOptions = getCodeOptions(this.props.observationByCode);

    this.statusOptions = StatusObservationCodes.reduce((acc, code) => {
      const ob = props.observationByCode.get(code);

      if (ob) {
        acc.push(mapObservationToOption(ob));
      }

      return acc;
    }, []);
  }

  toggleExtraFields = () => {
    this.setState(prevState => {
      const expanded = !prevState.expanded;
      const value =
        expanded === false
          ? this.helper.clearHiddenFields(prevState.value)
          : prevState.value;

      return {
        expanded,
        value,
      };
    });
  };

  handleSubmitClick = () => {
    Analytics.event({
      eventCategory: `${this.props.label} Search`,
      eventAction: 'Search',
    });

    this.submit();
  };

  submit() {
    const { onSubmit, clientId } = this.props;
    const { value: _value } = this.state;

    const value = _value.withMutations(v => {
      v.set('client_id', String(clientId));

      if (typeof onSubmit === 'function') {
        v = onSubmit(v);
      }
    });

    this.props.submitSearch(this.helper.serializeToObject(value));
  }

  handleChange = (unsanitizedValue: SearchFormValue) => {
    const value = this.helper.sanitize(this.state.value, unsanitizedValue);
    this.setState({ value });
  };

  handleReset = () => {
    this.setState(createDefaultState(this.helper));
  };

  handleLocationSearchChange = (search: string) => {
    this.setState(createStateFromSearch(this.helper, search), this.submit);
  };

  componentDidUpdate(prevProps: Props) {
    const {
      isSearching,
      clientId,
      query,
      location: { search },
      match,
    } = this.props;

    const didBeginSearch =
      isSearching === true && prevProps.isSearching === false;

    // begin search
    if (didBeginSearch) {
      const queryString = '?' + SearchFormHelper.convertObjectToURI(query);
      if (search !== queryString) {
        const nextRoute = `${match.url}/results${queryString}`;
        this.props.history.push(nextRoute);
      }
    }

    if (isSearching && prevProps.clientId !== clientId) {
      // Catalog options are dependent on clients, we need to invalidate this search if the catalog param is used
      // this behavior might be confusing to users we should either always invalidate or display a notification
      if (search.indexOf(catalogFieldConfig.name) > 0) {
        this.setState(
          prevState => ({
            value: prevState.value.remove(catalogFieldConfig.name),
          }),
          this.props.history.replace(this.props.match.url)
        );
      } else {
        this.submit();
      }
    }

    if (prevProps.clientId !== clientId) {
      this.codeOptions = getCodeOptions(this.props.observationByCode);
      this.setState(prevState => ({
        value: prevState.value
          .remove(catalogFieldConfig.name)
          .remove(codesIncluded.name)
          .remove(codesExcluded.name),
      }));
    }
  }

  getCodesFieldConfigOverrides() {
    const { value } = this.state;
    const { name: codesIncludedName } = codesIncluded;
    const { name: codesExcludedName } = codesExcluded;

    const codesIncludedOptions = getAvailableOptions(
      this.codeOptions,
      value.get(codesExcludedName)
    );
    const codesExcludedOptions = getAvailableOptions(
      this.codeOptions,
      value.get(codesIncludedName)
    );

    return {
      [codesIncludedName]: {
        options: codesIncludedOptions,
        placeholder:
          codesIncludedOptions.length === this.codeOptions.length
            ? 'Any Code'
            : '',
      },
      [codesExcludedName]: {
        options: codesExcludedOptions,
      },
    };
  }

  getStatusFieldConfigOverrides() {
    const { value } = this.state;
    const { name: statusIncludedName } = statusIncluded;
    const { name: statusExcludedName } = statusExcluded;

    const includedOptions = getAvailableOptions(
      this.statusOptions,
      value.get(statusExcludedName)
    );
    const excludedOptions = getAvailableOptions(
      this.statusOptions,
      value.get(statusIncludedName)
    );

    return {
      [statusIncludedName]: {
        options: includedOptions,
        placeholder:
          includedOptions.length === this.statusOptions.length
            ? 'Any Code'
            : '',
      },
      [statusExcludedName]: {
        options: excludedOptions,
      },
    };
  }

  getCatalogFieldConfigOverrides(catalogs: *) {
    const options =
      catalogs instanceof List
        ? catalogs
            .map(item => ({
              value: item.catalog_id,
              label: item.name + (item.locale ? ' - ' + item.locale : ''),
            }))
            .toArray()
        : [];

    return {
      [catalogFieldConfig.name]: {
        options: options.length === 1 ? [] : options,
        placeholder:
          options.length === 1
            ? [options[0].label]
            : catalogFieldConfig.placeholder,
      },
    };
  }

  render() {
    const {
      match,
      label,
      primaryFieldsets,
      extraFieldsets,
      clientId,
      copy = null,
      CardComponent = ClientModerationCard,
      catalogs,
    } = this.props;

    const fieldConfigOverrides = {
      ...this.getCodesFieldConfigOverrides(),
      ...this.getStatusFieldConfigOverrides(),
      ...this.getCatalogFieldConfigOverrides(catalogs),
    };

    const isUsingNewSearch = window.location.href.includes('-search');
    const method = isUsingNewSearch ? ugcSearch : nativeSearch;

    return (
      <div className="mv4" data-translate={true}>
        <Switch>
          <Route
            exact={true}
            path={match.url}
            render={() => {
              return (
                <React.Fragment>
                  {copy}
                  <SearchForm
                    primaryFieldsets={primaryFieldsets}
                    extraFieldsets={extraFieldsets}
                    value={this.state.value}
                    onSubmit={this.handleSubmitClick}
                    onReset={this.handleReset}
                    onChange={this.handleChange}
                    fieldConfigOverrides={fieldConfigOverrides}
                    expanded={this.state.expanded}
                    onExpand={this.toggleExtraFields}
                    isUsingNewSearch={isUsingNewSearch}
                  />
                </React.Fragment>
              );
            }}
          />
          <Route
            exact
            path={`${match.url}/results`}
            render={props => (
              <SearchCollection
                label={label}
                clientId={clientId}
                method={this.props.method || method}
                CardComponent={CardComponent}
                locationSearch={props.location.search}
                onLocationSearchChange={this.handleLocationSearchChange}
              />
            )}
          />
        </Switch>
      </div>
    );
  }
}

const mapStateToProps = (
  state: AppState & { search: SearchState },
  { clientId }
) => {
  const { appConfig } = state;
  const _isSearching = isSearching(state.search);
  const observationByCode = getObservationsFilteredByClient(
    clientId,
    appConfig
  );

  return {
    observationByCode,
    query: _isSearching ? state.search.queries.get(1) || {} : {},
    isSearching: _isSearching,
  };
};

const mapDispatchToProps: DispatchProps = {
  submitSearch,
};

const ConnectedSearchView = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchView);
export default (props: *) => {
  return (
    <ClientContext.Consumer>
      {({ clientId, catalogs }) => (
        <ConnectedSearchView
          clientId={clientId}
          catalogs={catalogs}
          {...props}
        />
      )}
    </ClientContext.Consumer>
  );
};

function getAvailableOptions(
  available: SelectOptions,
  restricted?: SelectOptions
) {
  if (!Array.isArray(restricted)) {
    return available;
  }

  const restrictedSet = new Set(restricted.map(option => option.value));
  return available.filter(({ value }) => !restrictedSet.has(value));
}

//
export function queryHacks(q: string): string {
  // change star_rating to between_star_rating
  return q.replace(/&star_rating(?==)/, '&between_star_rating');
}

function createStateFromSearch(
  helper: SearchFormHelper,
  search: string
): State {
  // get value and eventually show results
  const deserializedValue = helper.deserializeFromURI(queryHacks(search));
  const value = helper.bootstrapValues(deserializedValue);

  return {
    value,
    expanded: true,
  };
}

function createDefaultState(helper: SearchFormHelper): State {
  const today = new Date();
  const value = helper.bootstrapValues(
    Map({
      start_date: subWeeks(today, 1),
      end_date: today,
    })
  );

  return {
    value: value,
    expanded: false,
  };
}

function mapObservationToOption(ob: Observation) {
  return {
    value: ob.observation_type_code,
    label:
      ob.observation_type_code === Observation.PowerModerationCode
        ? 'PowerReviews Moderated'
        : ob.name,
  };
}

function getCodeOptions(observationByCodeMap) {
  return observationByCodeMap
    .filter(ob => ob.observation_type_code !== Observation.SecondOpinionCode)
    .map(mapObservationToOption)
    .toArray();
}

function getObservationsFilteredByClient(
  clientId,
  { observationsMap, observationCodeByClientIdMap }
) {
  const clientIdAsString = clientId.toString();

  const standardObservationCodes = observationCodeByClientIdMap.get(
    'global',
    []
  );
  const customObservationCodesByClient = observationCodeByClientIdMap.get(
    clientIdAsString,
    []
  );
  const availableObservationCodes = [
    ...standardObservationCodes,
    ...customObservationCodesByClient,
  ];

  return OrderedMap().withMutations(map => {
    availableObservationCodes.forEach(code => {
      const observation = observationsMap.get(code);

      if (observation) {
        map.set(code, observation);
      }
    });
  });
}



// WEBPACK FOOTER //
// ./src/containers/search/Search.js